// src/services/datev/SteuerIdAntragService.ts
//
// Frontend-Service für Steuer-ID-Anträge (Formular 010250) als Massenaktion je
// Mandant (XTAX GmbH & Co. KG, 14.10.2026).
//
// Kandidatenliste, Vorschau und Beauftragung laufen über die Netlify-Funktion
// steuerid-antrag (Fachlogik serverseitig, damit Vorschau, Prüfung und Versand
// identisch sind). Ausweise werden direkt in den privaten Speicherbereich
// steuerid-unterlagen hochgeladen und in steuerid_ausweise vermerkt; je
// Mitarbeiter gilt genau ein aktueller Ausweis.
//
// Zuordnung von Ausweisdateien (03.11.2026, Anlass erste Lieferung Thraki):
// Kunden benennen die Dateien oft nach der Mitarbeiternummer ihrer eigenen
// Software. In DATEV steht diese Nummer als Teil der betrieblichen
// Personalnummer vor dem Punkt ("Kundennummer.DATEV-Personalnummer"). Die
// Zuordnung vergleicht deshalb Personalnummer, weitere Personalnummern
// derselben Person, die vollständige betriebliche Personalnummer und die
// Kundennummer. Zugeordnet wird nur bei genau einem passenden Kandidaten.
//
// ZIP-Archive (03.11.2026) werden im Browser entpackt. Übernommen werden nur
// PDF-, JPG- und PNG-Dateien; der Typ wird aus der Dateiendung bestimmt, weil
// Einträge eines Archivs keinen Typ mitbringen.
//
// Lesbarkeit (04.11.2026): Antrag und Ausweis werden seit dem 04.11.2026 zu
// einer gemeinsamen PDF zusammengeführt. Ein Ausweis wird deshalb schon beim
// Hochladen mit derselben Bibliothek (pdf-lib) geöffnet wie später auf dem
// Server. Verschlüsselte oder beschädigte Dateien werden abgelehnt, statt erst
// beim Versand zu scheitern.
//
// Vorschau (04.11.2026): Der Server legt die Vorschau unter ihrem sprechenden
// Dateinamen ab und liefert einen kurzlebigen Link, damit Anzeige und Speichern
// im Browser diesen Namen tragen.

import JSZip from 'jszip';
import { PDFDocument } from 'pdf-lib';
import { supabase } from '../../shared/services/supabase';
import { netlifyFetch } from '../../shared/services/netlifyFetch';

const ENDPOINT = '/.netlify/functions/steuerid-antrag';
const BUCKET = 'steuerid-unterlagen';
export const AUSWEIS_MAX_BYTES = 20 * 1024 * 1024;
export const AUSWEIS_TYPEN: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPG',
  'image/png': 'PNG'
};

// Dateiendung (klein geschrieben) auf den Dateityp für Einträge aus ZIP-Archiven.
const TYP_NACH_ENDUNG: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png'
};

export interface SteuerIdKandidat {
  datev_employee_id: string;
  personalnummer: string;
  betriebliche_nr: string | null;
  // Weitere Personalnummern derselben Person (Wiedereintritte), der Antrag
  // läuft über diesen Datensatz.
  weitere_personalnummern: string[];
  nachname: string;
  vorname: string;
  land: string | null;
  // Austrittsdatum (YYYY-MM-DD), wenn der Mitarbeiter im laufenden Jahr
  // ausgetreten ist; null bei aktiven Mitarbeitern.
  ausgetreten_am: string | null;
  ausweis: { dateiname: string; hochgeladen_am: string } | null;
  letzter_antrag: {
    status: 'wartend' | 'in_arbeit' | 'versendet' | 'fehlgeschlagen' | 'abgebrochen';
    versendet_am: string | null;
    last_error: string | null;
    created_at: string;
    empfaenger: string | null;
  } | null;
  empfaenger: string | null;
  fehlende: string[];
  bereit: boolean;
}

export interface SteuerIdErledigt {
  personalnummer: string;
  name: string;
  versendet_am: string | null;
  ausweis_vorhanden: boolean;
}

export interface SteuerIdAuftrag {
  id: string;
  status: 'laeuft' | 'abgeschlossen' | 'abgebrochen';
  ausgeloest_von_email: string;
  anzahl_antraege: number;
  last_error: string | null;
  created_at: string;
  abgeschlossen_am: string | null;
  zaehler: { wartend: number; in_arbeit: number; versendet: number; fehlgeschlagen: number; abgebrochen: number };
}

export interface SteuerIdUebersicht {
  finanzamt: string | null;
  einrichtung_probleme: string[];
  spiegel_stand: string | null;
  kandidaten: SteuerIdKandidat[];
  erledigt: SteuerIdErledigt[];
  auftraege: SteuerIdAuftrag[];
}

export class SteuerIdAntragFehler extends Error {
  details: string[];
  constructor(message: string, details?: string[]) {
    super(message);
    this.name = 'SteuerIdAntragFehler';
    this.details = details || [];
  }
}

async function aufruf<T>(body: Record<string, unknown>): Promise<T> {
  const res = await netlifyFetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch (_e) {
    data = null;
  }
  if (!res.ok || !data?.success) {
    throw new SteuerIdAntragFehler(
      data?.error || `Anfrage fehlgeschlagen (HTTP ${res.status})`,
      data?.details || data?.fehlende
    );
  }
  return data as T;
}

/** Dateiname für den Speicherpfad: nur unkritische Zeichen. */
function sichererDateiname(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 100);
}

/** Vergleichswert einer Nummer: Großbuchstaben, ohne führende Nullen. */
function normalisiereNummer(wert: string | null | undefined): string {
  const s = String(wert ?? '').trim().toUpperCase();
  if (!s) return '';
  const ohneNullen = s.replace(/^0+/, '');
  return ohneNullen || '0';
}

/** Kundennummer: Teil der betrieblichen Personalnummer vor dem ersten Punkt. */
function kundennummer(betrieblicheNr: string | null): string {
  const wert = String(betrieblicheNr ?? '').trim();
  const punkt = wert.indexOf('.');
  return punkt > 0 ? normalisiereNummer(wert.slice(0, punkt)) : '';
}

/**
 * Ordnet eine Datei anhand ihres Namens genau einem Kandidaten zu. Gesucht
 * werden Personalnummer, weitere Personalnummern derselben Person,
 * betriebliche Personalnummer und Kundennummer jeweils als eigenständige
 * Zeichenfolge im Dateinamen ("123" passt nicht in "1234").
 * Liefert den Kandidaten nur bei genau einem Treffer, sonst null.
 */
export function ordneDateiZu(dateiname: string, kandidaten: SteuerIdKandidat[]): SteuerIdKandidat | null {
  const ohneEndung = dateiname.replace(/\.[^.]+$/, '');
  const teile = new Set(
    ohneEndung
      .split(/[^A-Za-z0-9]+/)
      .map(normalisiereNummer)
      .filter(Boolean)
  );
  const treffer = kandidaten.filter(k => {
    const nummern = [
      normalisiereNummer(k.personalnummer),
      ...(k.weitere_personalnummern || []).map(normalisiereNummer),
      normalisiereNummer(k.betriebliche_nr),
      kundennummer(k.betriebliche_nr)
    ].filter(Boolean);
    return nummern.some(n => teile.has(n));
  });
  return treffer.length === 1 ? treffer[0] : null;
}

export function pruefeAusweisDatei(datei: File): string | null {
  if (!AUSWEIS_TYPEN[datei.type]) return `${datei.name}: nur PDF, JPG oder PNG erlaubt.`;
  if (datei.size > AUSWEIS_MAX_BYTES) return `${datei.name}: größer als 20 MB.`;
  if (datei.size === 0) return `${datei.name}: Datei ist leer.`;
  return null;
}

/**
 * Prüft, ob sich ein Ausweis in die gemeinsame PDF übernehmen lässt.
 * Liefert eine Fehlermeldung oder null.
 */
export async function pruefeAusweisLesbar(datei: File): Promise<string | null> {
  const bytes = new Uint8Array(await datei.arrayBuffer());
  try {
    if (datei.type === 'application/pdf') {
      await PDFDocument.load(bytes);
    } else {
      const doc = await PDFDocument.create();
      if (datei.type === 'image/png') await doc.embedPng(bytes);
      else await doc.embedJpg(bytes);
    }
    return null;
  } catch (_e) {
    return datei.type === 'application/pdf'
      ? `${datei.name}: Die PDF ist verschlüsselt oder beschädigt und kann nicht in den Antrag übernommen werden.`
      : `${datei.name}: Das Bild kann nicht gelesen und deshalb nicht in den Antrag übernommen werden.`;
  }
}

/** Ist die Datei ein ZIP-Archiv (Endung oder Dateityp)? */
export function istZipArchiv(datei: File): boolean {
  return /\.zip$/i.test(datei.name)
    || datei.type === 'application/zip'
    || datei.type === 'application/x-zip-compressed';
}

/**
 * Entpackt ein ZIP-Archiv mit Ausweisen. Liefert die Dateien mit Typ aus der
 * Endung sowie die Namen übersprungener Einträge (andere Dateitypen).
 * Ordner, Systemdateien von macOS und versteckte Dateien werden ohne Hinweis
 * ausgelassen.
 */
export async function entpackeAusweisArchiv(archiv: File): Promise<{ dateien: File[]; uebersprungen: string[] }> {
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(archiv);
  } catch (_e) {
    throw new SteuerIdAntragFehler(`${archiv.name}: Das Archiv konnte nicht gelesen werden.`);
  }

  const dateien: File[] = [];
  const uebersprungen: string[] = [];
  const eintraege = Object.values(zip.files);
  for (const eintrag of eintraege) {
    if (eintrag.dir) continue;
    const pfad = eintrag.name;
    const name = pfad.split('/').pop() || pfad;
    if (pfad.startsWith('__MACOSX/') || name.startsWith('.')) continue;

    const endung = (name.split('.').pop() || '').toLowerCase();
    const typ = TYP_NACH_ENDUNG[endung];
    if (!typ) {
      uebersprungen.push(name);
      continue;
    }
    const inhalt = await eintrag.async('blob');
    dateien.push(new File([inhalt], name, { type: typ }));
  }
  return { dateien, uebersprungen };
}

export const steuerIdAntragService = {
  async ladeUebersicht(customerId: string): Promise<SteuerIdUebersicht> {
    return aufruf<SteuerIdUebersicht>({ mode: 'kandidaten', customer_id: customerId });
  },

  async ladeVorschau(customerId: string, datevEmployeeId: string): Promise<{ url: string; dateiname: string; ohne_ausweis: boolean }> {
    return aufruf<{ url: string; dateiname: string; ohne_ausweis: boolean }>({
      mode: 'vorschau',
      customer_id: customerId,
      datev_employee_id: datevEmployeeId
    });
  },

  async beauftrage(
    customerId: string,
    datevEmployeeIds: string[],
    absenderStaffId: string
  ): Promise<{ auftrag_id: string; anzahl: number; absender: string }> {
    const identityToken = localStorage.getItem('authToken');
    if (!identityToken) {
      throw new SteuerIdAntragFehler('Keine Anmelde-Identität gefunden. Bitte im Intranet neu anmelden.');
    }
    return aufruf<{ auftrag_id: string; anzahl: number; absender: string }>({
      mode: 'beauftragen',
      customer_id: customerId,
      datev_employee_ids: datevEmployeeIds,
      absender_staff_id: absenderStaffId,
      identity_token: identityToken
    });
  },

  /**
   * Lädt den Ausweis eines Mitarbeiters hoch und ersetzt einen vorhandenen.
   * Der alte Speicherinhalt wird erst nach erfolgreichem Vermerk entfernt.
   */
  async ladeAusweisHoch(customerId: string, personalnummer: string, datei: File, email?: string): Promise<void> {
    const fehler = pruefeAusweisDatei(datei);
    if (fehler) throw new SteuerIdAntragFehler(fehler);
    const nichtLesbar = await pruefeAusweisLesbar(datei);
    if (nichtLesbar) throw new SteuerIdAntragFehler(nichtLesbar);

    const pfad = `ausweise/${customerId}/${sichererDateiname(personalnummer)}/${Date.now()}_${sichererDateiname(datei.name)}`;
    const { error: uploadFehler } = await supabase.storage.from(BUCKET).upload(pfad, datei, {
      contentType: datei.type,
      upsert: false
    });
    if (uploadFehler) throw new SteuerIdAntragFehler(`${datei.name}: Upload fehlgeschlagen (${uploadFehler.message}).`);

    const { data: alt, error: altFehler } = await supabase
      .from('steuerid_ausweise')
      .select('storage_path')
      .eq('customer_id', customerId)
      .eq('personalnummer', personalnummer)
      .maybeSingle();
    if (altFehler) {
      await supabase.storage.from(BUCKET).remove([pfad]);
      throw new SteuerIdAntragFehler(`${datei.name}: Vermerk konnte nicht geprüft werden (${altFehler.message}).`);
    }

    const { error: vermerkFehler } = await supabase
      .from('steuerid_ausweise')
      .upsert({
        customer_id: customerId,
        personalnummer,
        storage_path: pfad,
        dateiname: datei.name,
        content_type: datei.type,
        hochgeladen_von_email: email ?? null
      }, { onConflict: 'customer_id,personalnummer' });
    if (vermerkFehler) {
      await supabase.storage.from(BUCKET).remove([pfad]);
      throw new SteuerIdAntragFehler(`${datei.name}: Vermerk fehlgeschlagen (${vermerkFehler.message}).`);
    }

    if (alt?.storage_path && alt.storage_path !== pfad) {
      const { error: entfernFehler } = await supabase.storage.from(BUCKET).remove([alt.storage_path]);
      if (entfernFehler) {
        console.warn('Alter Ausweis konnte nicht entfernt werden:', entfernFehler.message);
      }
    }
  }
};
